import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API, getAuthHeaders } from '../App';
import { Navigation } from '../components/Navigation';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ArrowLeft, CalendarIcon, Save, Target, Info, Search, MapPin, Loader2, X, Users, Check } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function PostRound() {
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [date, setDate] = useState(new Date());
  const [formData, setFormData] = useState({
    course_name: '',
    score: '',
    holes_played: '18',
    course_rating: '',
    slope_rating: '',
    notes: '',
    photo_url: ''
  });

  // Course search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseDetails, setCourseDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [selectedTee, setSelectedTee] = useState(null);
  const [apiAvailable, setApiAvailable] = useState(true);
  const searchTimeout = useRef(null);
  const searchRef = useRef(null);

  // Playing partners (Bundle members who played this round with the user)
  const [partnerSearch, setPartnerSearch] = useState('');
  const [partnerResults, setPartnerResults] = useState([]);
  const [selectedPartners, setSelectedPartners] = useState([]); // [{user_id, name, picture}]
  const [searchingPartners, setSearchingPartners] = useState(false);
  const partnerSearchTimeout = useRef(null);

  // Debounced member search
  useEffect(() => {
    if (partnerSearchTimeout.current) clearTimeout(partnerSearchTimeout.current);
    const q = partnerSearch.trim();
    if (q.length < 2) { setPartnerResults([]); return; }
    partnerSearchTimeout.current = setTimeout(async () => {
      setSearchingPartners(true);
      try {
        const res = await fetch(`${API}/members/search?q=${encodeURIComponent(q)}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : (data?.members || []);
          setPartnerResults(list.filter(m => m.user_id !== user?.user_id));
        }
      } catch { /* ignore */ } finally { setSearchingPartners(false); }
    }, 250);
    return () => partnerSearchTimeout.current && clearTimeout(partnerSearchTimeout.current);
  }, [partnerSearch, user?.user_id]);

  const addPartner = (member) => {
    if (selectedPartners.some(p => p.user_id === member.user_id)) return;
    setSelectedPartners(prev => [...prev, { user_id: member.user_id, name: member.name, picture: member.picture }]);
    setPartnerSearch('');
    setPartnerResults([]);
  };
  const removePartner = (user_id) => setSelectedPartners(prev => prev.filter(p => p.user_id !== user_id));

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
    if (!loading && user && !user.is_member) {
      toast.error('Active membership required to post rounds');
      navigate('/membership');
    }
  }, [loading, user, navigate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search courses with debounce
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await fetch(`${API}/courses/search?query=${encodeURIComponent(searchQuery)}`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const courses = await response.json();
          setSearchResults(courses);
          setShowResults(true);
          setApiAvailable(true);
        } else if (response.status === 503) {
          setApiAvailable(false);
          setSearchResults([]);
        }
      } catch (error) {
        console.error('Course search error:', error);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [searchQuery]);

  // Fetch course details when a course is selected
  const fetchCourseDetails = async (courseId) => {
    setLoadingDetails(true);
    setCourseDetails(null);
    setSelectedTee(null);
    
    try {
      const response = await fetch(`${API}/courses/${courseId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const details = await response.json();
        setCourseDetails(details);
        
        // Auto-select first male tee if available
        if (details.tees?.male?.length > 0) {
          const firstTee = details.tees.male[0];
          setSelectedTee(firstTee);
          setFormData(prev => ({
            ...prev,
            course_rating: firstTee.course_rating?.toString() || '',
            slope_rating: firstTee.slope_rating?.toString() || ''
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching course details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    setSearchQuery(course.course_name || course.club_name);
    setFormData(prev => ({
      ...prev,
      course_name: course.course_name || course.club_name
    }));
    setShowResults(false);
    fetchCourseDetails(course.id);
  };

  const handleTeeSelect = (tee) => {
    setSelectedTee(tee);
    setFormData(prev => ({
      ...prev,
      course_rating: tee.course_rating?.toString() || '',
      slope_rating: tee.slope_rating?.toString() || ''
    }));
  };

  const handleManualEntry = () => {
    setFormData(prev => ({
      ...prev,
      course_name: searchQuery
    }));
    setShowResults(false);
    setSelectedCourse(null);
    setCourseDetails(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user.is_member) {
      toast.error('Active membership required to post rounds');
      navigate('/membership');
      return;
    }

    // Use searchQuery if formData.course_name is empty
    const courseName = formData.course_name || searchQuery;
    
    if (!courseName || !formData.score) {
      toast.error('Please fill in course name and score');
      return;
    }

    setSubmitting(true);

    // Peer-verification: at least one Bundle member must have played this round with the user
    if (selectedPartners.length === 0) {
      toast.error('Add at least one Bundle member you played with — scores can only be posted with a member on the round.');
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${API}/rounds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: format(date, 'yyyy-MM-dd'),
          course_name: courseName,
          score: parseInt(formData.score),
          holes_played: parseInt(formData.holes_played),
          course_rating: formData.course_rating ? parseFloat(formData.course_rating) : null,
          slope_rating: formData.slope_rating ? parseInt(formData.slope_rating) : null,
          notes: formData.notes || null,
          photo_url: formData.photo_url || null,
          playing_partners: selectedPartners.map(p => p.user_id),
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.handicap_differential) {
          toast.success(`Round posted! Handicap differential: ${result.handicap_differential}`);
        } else {
          toast.success('Round posted successfully!');
        }
        navigate('/dashboard');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to post round');
      }
    } catch (error) {
      toast.error('An error occurred');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green"></div>
      </div>
    );
  }

  // Get all available tees
  const availableTees = courseDetails ? [
    ...(courseDetails.tees?.male || []).map(t => ({ ...t, gender: 'Men' })),
    ...(courseDetails.tees?.female || []).map(t => ({ ...t, gender: 'Women' }))
  ] : [];

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav md:pb-8">
      <Navigation user={user} />

      <div className="max-w-2xl mx-auto px-4 md:px-8 pt-20 md:pt-24">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4 text-bundle-green hover:bg-bundle-cream-dark -ml-2"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        <div className="mb-6">
          <h1 className="font-heading font-bold text-3xl text-bundle-green mb-2">Post a Round</h1>
          <p className="font-body text-gray-600">Record your golf round for handicap tracking</p>
        </div>

        {/* Fast path: Scan the scorecard instead of entering manually */}
        <div className="mb-5 rounded-2xl bg-gradient-to-br from-purple-600 to-purple-800 text-white p-4 flex items-center gap-3" data-testid="scan-scorecard-cta">
          <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm">Faster: Scan your scorecard</p>
            <p className="text-xs text-white/80">Snap a photo — Bundle members are auto-linked from the players list.</p>
          </div>
          <Button
            onClick={() => navigate('/dashboard?scan=1')}
            data-testid="open-scanner-btn"
            className="bg-white text-purple-700 hover:bg-white/90 font-bold text-xs px-4"
          >
            Scan
          </Button>
        </div>

        <Card className="bg-white border-bundle-cream-dark">
          <CardContent className="p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Date */}
              <div className="space-y-2">
                <Label className="font-body text-bundle-green">Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      data-testid="date-picker-btn"
                      className="w-full justify-start text-left font-normal border-bundle-cream-dark hover:bg-bundle-cream/50"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-bundle-gold" />
                      {date ? format(date, 'PPP') : <span className="text-gray-400">Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={date} onSelect={setDate} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Course Search */}
              <div className="space-y-2" ref={searchRef}>
                <Label htmlFor="course_search" className="font-body text-bundle-green">Course Name *</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="course_search"
                    data-testid="course-name-input"
                    placeholder="Search for a golf course..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setFormData(prev => ({ ...prev, course_name: e.target.value }));
                      setSelectedCourse(null);
                      setCourseDetails(null);
                    }}
                    onFocus={() => searchResults.length > 0 && setShowResults(true)}
                    className="pl-10 border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                  />
                  {searching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bundle-green animate-spin" />
                  )}
                </div>
                
                {/* Search Results Dropdown */}
                {showResults && searchResults.length > 0 && (
                  <div className="absolute z-50 w-full max-w-[calc(100%-3rem)] bg-white border border-bundle-cream-dark rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {searchResults.map((course) => (
                      <button
                        key={course.id}
                        type="button"
                        onClick={() => handleCourseSelect(course)}
                        className="w-full text-left px-4 py-3 hover:bg-bundle-cream/50 border-b border-bundle-cream-dark last:border-b-0 transition-colors"
                      >
                        <p className="font-body font-semibold text-bundle-green text-sm">
                          {course.course_name || course.club_name}
                        </p>
                        {course.location?.address && (
                          <p className="font-body text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3 h-3" />
                            {course.location.city}, {course.location.state}
                          </p>
                        )}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={handleManualEntry}
                      className="w-full text-left px-4 py-3 hover:bg-bundle-cream/50 text-bundle-gold font-body text-sm"
                    >
                      + Use "{searchQuery}" as course name
                    </button>
                  </div>
                )}

                {/* Manual entry hint when API not available */}
                {!apiAvailable && searchQuery.length >= 2 && (
                  <p className="text-xs text-gray-500 font-body">
                    Course lookup unavailable. You can enter the course name manually.
                  </p>
                )}
              </div>

              {/* Selected Course Info & Tee Selection */}
              {loadingDetails && (
                <div className="bg-bundle-cream/50 rounded-xl p-4 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 text-bundle-green animate-spin mr-2" />
                  <span className="font-body text-sm text-gray-600">Loading course details...</span>
                </div>
              )}

              {courseDetails && !loadingDetails && (
                <div className="bg-bundle-green/5 border border-bundle-green/20 rounded-xl p-4 space-y-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-body font-semibold text-bundle-green">
                        {courseDetails.course_name || courseDetails.club_name}
                      </p>
                      {courseDetails.location && (
                        <p className="font-body text-xs text-gray-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {courseDetails.location.city}, {courseDetails.location.state}
                        </p>
                      )}
                    </div>
                    <span className="bg-bundle-green text-white text-xs px-2 py-1 rounded-full font-body">
                      Auto-filled
                    </span>
                  </div>

                  {/* Tee Selection */}
                  {availableTees.length > 0 && (
                    <div className="space-y-2">
                      <Label className="font-body text-bundle-green text-sm">Select Tee</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {availableTees.map((tee, idx) => (
                          <button
                            key={`${tee.tee_name}-${tee.gender}-${idx}`}
                            type="button"
                            onClick={() => handleTeeSelect(tee)}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              selectedTee === tee
                                ? 'border-bundle-green bg-bundle-green/10'
                                : 'border-bundle-cream-dark hover:border-bundle-green/50'
                            }`}
                          >
                            <p className="font-body font-semibold text-sm text-bundle-green">
                              {tee.tee_name}
                            </p>
                            <p className="font-body text-xs text-gray-500">{tee.gender}</p>
                            <p className="font-body text-xs text-gray-600 mt-1">
                              {tee.total_yards} yds • {tee.slope_rating} slope
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Score and Holes */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="score" className="font-body text-bundle-green">Score *</Label>
                  <Input
                    id="score"
                    type="number"
                    data-testid="score-input"
                    placeholder="72"
                    value={formData.score}
                    onChange={(e) => setFormData({ ...formData, score: e.target.value })}
                    className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-bundle-green">Holes Played</Label>
                  <Select
                    value={formData.holes_played}
                    onValueChange={(value) => setFormData({ ...formData, holes_played: value })}
                  >
                    <SelectTrigger data-testid="holes-select" className="border-bundle-cream-dark">
                      <SelectValue placeholder="Select holes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="9">9 Holes</SelectItem>
                      <SelectItem value="18">18 Holes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* USGA Handicap Fields */}
              <div className="bg-bundle-cream/50 rounded-xl p-4 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-5 h-5 text-bundle-gold" />
                  <span className="font-body font-semibold text-bundle-green">USGA Handicap Data</span>
                  {selectedTee && (
                    <span className="text-xs text-bundle-green font-body bg-bundle-green/10 px-2 py-0.5 rounded-full">
                      Auto-filled from {selectedTee.tee_name} tees
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="course_rating" className="font-body text-bundle-green text-sm">Course Rating</Label>
                    <Input
                      id="course_rating"
                      type="number"
                      step="0.1"
                      data-testid="course-rating-input"
                      placeholder="e.g., 72.5"
                      value={formData.course_rating}
                      onChange={(e) => setFormData({ ...formData, course_rating: e.target.value })}
                      className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="slope_rating" className="font-body text-bundle-green text-sm">Slope Rating</Label>
                    <Input
                      id="slope_rating"
                      type="number"
                      data-testid="slope-rating-input"
                      placeholder="e.g., 135"
                      value={formData.slope_rating}
                      onChange={(e) => setFormData({ ...formData, slope_rating: e.target.value })}
                      className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                    />
                  </div>
                </div>
                
                <p className="text-xs text-gray-500 font-body flex items-start gap-1">
                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  {courseDetails 
                    ? "Course rating and slope auto-filled from selected tee. You can adjust if needed."
                    : "Search for a course above to auto-fill, or enter manually."}
                </p>
              </div>

              {/* Photo URL */}
              <div className="space-y-2">
                <Label htmlFor="photo_url" className="font-body text-bundle-green">Photo URL (Optional)</Label>
                <Input
                  id="photo_url"
                  type="url"
                  data-testid="photo-url-input"
                  placeholder="https://example.com/photo.jpg"
                  value={formData.photo_url}
                  onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                  className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes" className="font-body text-bundle-green">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  data-testid="notes-input"
                  placeholder="Any notes about your round..."
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                />
              </div>

              {/* Playing Partners — MANDATORY: at least one Bundle member must be tagged */}
              <div className="space-y-2 bg-bundle-green/5 border-2 border-bundle-green/20 rounded-xl p-4" data-testid="playing-partners-section">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-bundle-green" />
                  <Label className="font-body text-bundle-green font-bold">Playing Partners *</Label>
                  <span className="text-[10px] font-bold bg-bundle-gold/20 text-bundle-gold-dark px-2 py-0.5 rounded-full">Required</span>
                </div>
                <p className="text-xs text-gray-600 -mt-1 mb-2">
                  Tag at least one Bundle member who played this round with you. Scores can only be posted with a member on the round.
                </p>

                {selectedPartners.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2" data-testid="selected-partners">
                    {selectedPartners.map(p => (
                      <span key={p.user_id} data-testid={`partner-chip-${p.user_id}`} className="inline-flex items-center gap-1.5 bg-bundle-green text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                        <Check className="w-3 h-3" />
                        {p.name}
                        <button type="button" onClick={() => removePartner(p.user_id)} className="hover:bg-white/20 rounded-full p-0.5" aria-label={`Remove ${p.name}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    data-testid="partner-search-input"
                    placeholder="Search Bundle members by name or email..."
                    value={partnerSearch}
                    onChange={(e) => setPartnerSearch(e.target.value)}
                    className="pl-9 border-bundle-cream-dark focus:border-bundle-green"
                  />
                  {searchingPartners && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-bundle-green" />
                  )}
                </div>

                {partnerResults.length > 0 && (
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 bg-white max-h-56 overflow-y-auto" data-testid="partner-search-results">
                    {partnerResults.slice(0, 8).map(m => {
                      const already = selectedPartners.some(p => p.user_id === m.user_id);
                      return (
                        <button
                          key={m.user_id}
                          type="button"
                          onClick={() => !already && addPartner(m)}
                          disabled={already}
                          data-testid={`partner-result-${m.user_id}`}
                          className={`w-full px-3 py-2 flex items-center gap-2 text-left transition-colors ${already ? 'bg-bundle-green/10 cursor-default' : 'hover:bg-bundle-cream/60'}`}
                        >
                          <div className="w-8 h-8 rounded-full bg-bundle-green text-bundle-gold text-xs font-bold flex items-center justify-center flex-shrink-0">
                            {(m.name || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-bundle-green truncate">{m.name}</p>
                            <p className="text-[11px] text-gray-500 truncate">{m.email || ''}</p>
                          </div>
                          {already && <Check className="w-4 h-4 text-bundle-green" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Submit */}
              <Button
                type="submit"
                data-testid="submit-round-btn"
                disabled={submitting || selectedPartners.length === 0}
                className="w-full btn-bundle py-6"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Save Round
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
